import { BankLogo, Box, CardLogo, Icon, PaymentMethod, withStyles, Tooltip, BankingPaymentRail } from 'vopay';
import { useMemo } from 'react';
import styles from './PaymentMethodOption.module.scss';
import ConvenienceFeeWithTooltip from './ConvenienceFeeWithTooltip';
import { useToken } from '@/providers/Token.provider';
import { getBankingRailOptions } from '@/utils/bankingRailMapping';
import { ConvenienceFeeBreakdown } from 'UI';

const cn = withStyles(styles);

// Export helper to check if bank has multiple rails
export function getBankRailOptionsForPaymentMethod(data: PaymentMethod, allowedRails?: BankingPaymentRail[]) {
  if (data.PaymentMethod !== 'BankAccount') return [];

  const paymentRails = data.ExtraFields.PaymentRails;
  if (!paymentRails) return [];

  const railsArray =
    typeof paymentRails === 'string'
      ? paymentRails.split(',').map((r) => r.trim() as BankingPaymentRail)
      : Array.isArray(paymentRails)
        ? paymentRails
        : [];

  // Filter by allowed rails if provided
  const filteredRails =
    allowedRails && allowedRails.length > 0 ? railsArray.filter((rail) => allowedRails.includes(rail)) : railsArray;

  return getBankingRailOptions(filteredRails);
}

type PaymentMethodOptionProps = {
  data: PaymentMethod;
  isNested?: boolean;
  railOption?: { rail: BankingPaymentRail; label: string; description: string; tooltip: string };
  allowedRails?: BankingPaymentRail[];
};

export default function PaymentMethodOption({
  data,
  isNested = false,
  railOption,
  allowedRails,
}: PaymentMethodOptionProps) {
  const { token } = useToken();
  const province = data.ExtraFields.ProvinceAbbreviation || undefined;
  const country = data.ExtraFields.CountryAbbreviation || token?.Country;

  const bankRailOptions = useMemo(() => getBankRailOptionsForPaymentMethod(data, allowedRails), [data, allowedRails]);
  const showBankRailSelection = bankRailOptions.length > 1;
  console.log(data, railOption);

  // If this is a nested rail option, show the rail details
  if (isNested && railOption) {
    return (
      <div className={cn('bankRailNestedOption')}>
        <div className={cn('bankRailContent')}>
          <div className={cn('bankRailMainInfo')}>
            <span className={cn('bankRailLabel')}>
              {railOption.label}
              <Tooltip
                icon="info"
                variant="outlined"
                iconWidth="8px"
                buttonWidth="14px"
                position="top"
                information={railOption.tooltip}
                hasArrow
                width="280px"
              />
            </span>
          </div>
          <div className={cn('bankRailSubInfo')}>
            <ConvenienceFeeBreakdown paymentMethodType="BankAccount" bankingPaymentRail={railOption.rail} />
          </div>
        </div>
      </div>
    );
  }

  if (data.PaymentMethod === 'BankAccount') {
    // For bank accounts with multiple rails, return null here
    // The container will handle rendering the bank header + nested options
    if (showBankRailSelection) {
      return null;
    }

    // Single rail or no rail selection needed
    return (
      <Box gapSize="8px" jc="fs" ai="center">
        <div className={cn('iconContainer')}>
          {data.ExtraFields.InstitutionName === 'VOPAY TESTING BANK' ? (
            <Icon icon="vLogo" width="20px" height="20px" />
          ) : (
            <BankLogo width="24px" height="24px" financialInstitutionNumber={data.ExtraFields.BankID || ''} />
          )}
        </div>

        <div className={cn('details')}>
          <div className={cn('bankRow')}>
            <span>{data.ExtraFields.InstitutionName === '-' ? '' : data.ExtraFields.InstitutionName}</span>
            <span>{data.PaymentDetails?.slice(-7)}</span>
          </div>

          <ConvenienceFeeWithTooltip
            paymentMethodType={data.PaymentMethod}
            bankingPaymentRail={railOption?.rail}
            country={country}
            province={province}
          />
        </div>
      </Box>
    );
  }

  if (data.PaymentMethod === 'CreditCard' || data.PaymentMethod === 'DebitCard' || data.PaymentMethod === 'GooglePay') {
    return (
      <Box gapSize="8px" jc="fs" ai="center">
        <div className={cn('iconContainer')}>
          <CardLogo width="24px" height="24px" cardBrand={data.ExtraFields.CardBrand} />
        </div>

        <div className={cn('details')}>
          <span>****{data.PaymentDetails?.replaceAll('-', '').slice(-4)}</span>
          <ConvenienceFeeWithTooltip paymentMethodType={data.PaymentMethod} country={country} province={province} />
        </div>
      </Box>
    );
  }

  return (
    <Box gapSize="8px" jc="fs" ai="center">
      <Icon icon="interac2" color="none" width="24px" height="24px" />

      <div className={cn('details')}>
        <span>{data.PaymentDetails}</span>
        <ConvenienceFeeWithTooltip paymentMethodType={data.PaymentMethod} country={country} province={province} />
      </div>
    </Box>
  );
}
